import React, { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useStudyRuntime } from '../contexts/StudyRuntimeContext';
import { useStudyWorkspace } from '../contexts/StudyWorkspaceContext';
import { getStudyTabFromPath } from '../lib/studyNavigation';
import { warmStudyRoute } from '../lib/studyRouteWarmup';

const isPlainLeftClick = (event) => (
  event.button === 0 &&
  !event.defaultPrevented &&
  !event.metaKey &&
  !event.altKey &&
  !event.ctrlKey &&
  !event.shiftKey
);

const PreloadLink = ({
  children,
  to,
  prefetch = true,
  prefetchMode = 'hover',
  studyTabId = null,
  onMouseEnter,
  onFocus,
  onMouseDown,
  onTouchStart,
  onClick,
  ...props
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const studyRuntime = useStudyRuntime();
  const workspace = useStudyWorkspace();
  const resolvedStudyTabId = studyTabId || (typeof to === 'string' ? getStudyTabFromPath(to) : null);
  const canWarmStudyRoute = Boolean(prefetch && resolvedStudyTabId);

  const warmStudyIntent = useCallback((priority = 'intent') => {
    if (!canWarmStudyRoute) return;

    if (studyRuntime) {
      void studyRuntime.preloadTab(resolvedStudyTabId);
      return;
    }

    void warmStudyRoute(resolvedStudyTabId, { priority, workspace });
  }, [canWarmStudyRoute, resolvedStudyTabId, studyRuntime, workspace]);

  const handleMouseEnter = useCallback((event) => {
    onMouseEnter?.(event);
    if (event.defaultPrevented || prefetchMode === 'none') return;
    warmStudyIntent('intent');
  }, [onMouseEnter, prefetchMode, warmStudyIntent]);

  const handleFocus = useCallback((event) => {
    onFocus?.(event);
    if (event.defaultPrevented || prefetchMode === 'none') return;
    warmStudyIntent('intent');
  }, [onFocus, prefetchMode, warmStudyIntent]);

  const handleMouseDown = useCallback((event) => {
    onMouseDown?.(event);
    if (event.defaultPrevented) return;
    warmStudyIntent('commit');
  }, [onMouseDown, warmStudyIntent]);

  const handleTouchStart = useCallback((event) => {
    onTouchStart?.(event);
    if (event.defaultPrevented) return;
    warmStudyIntent('commit');
  }, [onTouchStart, warmStudyIntent]);

  const handleClick = useCallback((event) => {
    onClick?.(event);

    if (event.defaultPrevented || !resolvedStudyTabId) {
      return;
    }

    if (studyRuntime) {
      if (!isPlainLeftClick(event)) return;
      if (studyRuntime.pendingTab === resolvedStudyTabId && location.pathname !== to) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      studyRuntime.navigateToTab(resolvedStudyTabId);
      return;
    }

    if (!isPlainLeftClick(event) || typeof to !== 'string' || location.pathname === to) {
      return;
    }

    event.preventDefault();
    warmStudyRoute(resolvedStudyTabId, { priority: 'commit', workspace }).catch(() => {});
    navigate(to);
  }, [location.pathname, navigate, onClick, resolvedStudyTabId, studyRuntime, to, workspace]);

  return (
    <Link
      to={to}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  );
};

export { PreloadLink };
